'use client';
import classNames from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useScrollPosition } from '@/hooks/useScrollPosition';
import { SanityBlock } from '@/types/sanity.types';
import { sanityRenderBlockContent } from '@/utils/sanityBlockContent';

export type Tab = {
  title?: string;
  content?: string | SanityBlock[];
  current: boolean;
};

type TabsProps = {
  tabs: Tab[];
  name: string;
};

export const Tabs = (props: TabsProps) => {
  const [tabs, setTabs] = useState(props.tabs);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [isScrolledUp, setIsScrolledUp] = useState(typeof window === 'undefined' || !window.scrollY);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  useScrollPosition(({ isScrolledUp }) => setIsScrolledUp(isScrolledUp), []);

  const getCurrentTab = (): Tab | undefined => tabs.find(({ current }) => current);

  const getTabPath = (tab: Tab | undefined = getCurrentTab()) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params = new URLSearchParams(searchParams as any);
    if (tab?.title) {
      params.set(props.name, tab.title);
      return pathname + '?' + params.toString();
    }
  };

  useEffect(() => {
    if (loaded) {
      const path = getTabPath();
      path && router.push(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <>
      <nav
        className={classNames('sticky top-[4.5rem] z-20 px-5 py-1 sm:top-[7rem] sm:px-10', {
          'bg-background-200/80': !isScrolledUp,
          'dark:bg-background-900/80': !isScrolledUp,
          'backdrop-blur-lg': !isScrolledUp,
        })}
        aria-label="Tabs"
        suppressHydrationWarning
      >
        <div className="container mx-auto flex flex-col sm:flex-row sm:gap-8">
          {tabs.map((tab) => (
            <a
              key={tab.title}
              className={classNames(
                'dark:text-whit cursor-pointer whitespace-nowrap border-gray-700 px-1 py-4 text-base font-medium tracking-wide text-gray-700 dark:border-white dark:text-white sm:border-b-2',
                {
                  'border-transparent opacity-50 dark:opacity-30': !tab.current,
                },
              )}
              aria-current={tab.current ? 'page' : undefined}
              onClick={(event) => {
                setTabs((tabs) => tabs.map((t) => ({ ...t, current: t.title === tab.title })));
                window.scrollTo({ top: 0, behavior: 'smooth' });
                event.preventDefault();
              }}
              href={getTabPath(tab)}
            >
              {tab.title}
            </a>
          ))}
        </div>
      </nav>
      {tabs
        .filter(({ current }) => current)
        .map(({ title, content }) => (
          <div key={title} className="px-5 sm:px-10">
            <div className="container mx-auto py-10 text-justify text-gray-800 dark:text-white">
              {typeof content === 'string'
                ? content
                : sanityRenderBlockContent({
                  content,
                  cssClasses: {
                    h2: 'font-bold sm:text-xl lg:text-2xl opacity-75',
                    h3: 'font-bold sm:text-base lg:text-xl opacity-50 my-5',
                    h4: 'font-normal sm:text-base lg:text-xl opacity-50 my-5',
                    h5: 'font-bold my-3',
                    p: 'leading-6 sm:leading-8 text-sm sm:text-base',
                    em: 'italic',
                  },
                })}
            </div>
          </div>
        ))}
    </>
  );
};
